import cx from 'classnames';
import React, { useId } from 'react';
import { Controller } from 'react-hook-form';

import { Button, Input, Popup, PopupSection, Text, Textarea } from '@flixbus/honeycomb-react';

import LocationSearch from 'common/LocationSearch';
import OverlappingBanner from 'common/OverlappingActivitiesBanner/OverlappingBanner';
import WATooLongShiftErrorBanner from 'common/WATooLongShiftErrorBanner';

import { MODAL_SET_ON_PROTECTION_TEXT } from './text';
import { useModalSetOnProtection } from './useModalSetOnProtection';

import styles from './styles.module.css';

const ModalSetOnProtection = ({ readOnly = false, ...props }) => {
	const fromId = useId();
	const fromTimeId = useId();
	const tillId = useId();
	const tillTimeId = useId();
	const noteId = useId();

	const {
		control,
		miDay,
		minTillTime,
		maxTillTime,
		loading,
		isSubmitDisabled,
		isFromTimeDisabled,
		isTillDisabled,
		isTillTimeDisabled,
		hasOverlappingActivities,
		overlappingActivities,
		overlappingAction,
		isDelayOptionAvailable,
		cannotCancelOverlaps,
		hasShiftTimeTooLongError,
		isValidDate,
		validateYear,
		validateTillTime,
		setOverlappingAction,
		delayMinutes,
		delayError,
		minDelayMinutes,
		setDelayMinutes,
		setValue,
		handleFromDateChange,
		handleFromTimeChange,
		handleTillDateChange,
		handleTillTimeChange,
		handleResetSingleField,
		handleSubmit,
		handleClose,
	} = useModalSetOnProtection({ readOnly, ...props });

	return (
		<Popup
			extraClasses={styles.modalWrapper}
			id="modal-protection"
			active={true}
			onOverlayClick={handleClose}
		>
			<PopupSection type="title">
				{readOnly
					? MODAL_SET_ON_PROTECTION_TEXT.protectionDetails
					: MODAL_SET_ON_PROTECTION_TEXT.setOnProtectionTitle}
			</PopupSection>

			<PopupSection extraClasses={styles.scrollableContent} type="content">
				{!readOnly && (
					<Text extraClasses={styles.descriptionMargin}>
						{MODAL_SET_ON_PROTECTION_TEXT.setOnProtectionDescription}
					</Text>
				)}

				<form
					onSubmit={(e) => {
						e.preventDefault();
						handleSubmit();
					}}
				>
					<div className={styles.timeRow}>
						<div className={styles.timeWrapper}>
							<Controller
								name="from"
								control={control}
								rules={{
									required: true,
									validate: { year: validateYear, range: isValidDate },
								}}
								render={({ field: { ref: _ref, ...field }, fieldState }) => (
									<Input
										{...field}
										id={fromId}
										type="date"
										label={MODAL_SET_ON_PROTECTION_TEXT.startDateAndTime}
										readOnly={readOnly}
										valid={!fieldState.invalid}
										infoError={fieldState.error?.message}
										extraClasses={styles.dateInput}
										onChange={(e) => handleFromDateChange(field, e)}
									/>
								)}
							/>
							<Controller
								name="fromTime"
								control={control}
								rules={{ required: true }}
								disabled={isFromTimeDisabled}
								render={({ field: { ref: _ref, ...field } }) => (
									<Input
										{...field}
										id={fromTimeId}
										type="time"
										aria-label="Start time"
										readOnly={readOnly}
										extraClasses={styles.timeInput}
										onChange={(e) => handleFromTimeChange(field, e)}
									/>
								)}
							/>
						</div>

						<div className={styles.timeWrapper}>
							<Controller
								name="till"
								control={control}
								rules={{
									required: true,
									validate: { year: validateYear, range: isValidDate },
								}}
								render={({ field: { ref: _ref, ...field }, fieldState }) => (
									<Input
										{...field}
										id={tillId}
										type="date"
										min={miDay}
										disabled={isTillDisabled}
										readOnly={readOnly}
										label={MODAL_SET_ON_PROTECTION_TEXT.endDateAndTime}
										valid={!fieldState.invalid}
										infoError={fieldState.error?.message}
										extraClasses={styles.dateInput}
										onChange={(e) => handleTillDateChange(field, e)}
									/>
								)}
							/>
							<Controller
								name="tillTime"
								control={control}
								rules={{ required: true, validate: validateTillTime }}
								disabled={isTillTimeDisabled}
								render={({ field: { ref: _ref, ...field }, fieldState }) => (
									<Input
										{...field}
										id={tillTimeId}
										type="time"
										aria-label="End time"
										placeholder="hh:mm"
										readOnly={readOnly}
										min={minTillTime}
										max={maxTillTime}
										valid={!fieldState.invalid}
										infoError={fieldState.error?.message}
										extraClasses={styles.timeInput}
										onChange={(e) => handleTillTimeChange(field, e)}
									/>
								)}
							/>
						</div>
					</div>

					<div className={cx(styles.row, styles.full)}>
						<Controller
							name="location"
							control={control}
							rules={{ required: MODAL_SET_ON_PROTECTION_TEXT.fromLocation }}
							render={({ field }) => (
								<LocationSearch
									fieldControl={field}
									fieldToReset="location"
									readOnly={readOnly}
									setValue={setValue}
									onReset={handleResetSingleField}
									searchOnlyWithStations
								/>
							)}
						/>
					</div>

					<div className={cx(styles.row, styles.full)}>
						<Controller
							name="note"
							control={control}
							rules={{ required: false }}
							render={({ field: { ref: _ref, ...field } }) => (
								<Textarea
									{...field}
									id={noteId}
									label="Note"
									placeholder="Additional information"
									readOnly={readOnly}
									onChange={(e) => field.onChange(e.target.value)}
								/>
							)}
						/>
					</div>

					{hasShiftTimeTooLongError && <WATooLongShiftErrorBanner />}

					{hasOverlappingActivities && (
						<OverlappingBanner
							overlappingList={overlappingActivities}
							overlappingAction={overlappingAction}
							cannotCancelOverlaps={cannotCancelOverlaps}
							isDelayOptionAvailable={isDelayOptionAvailable}
							onOverlappingActionChange={setOverlappingAction}
							delayMinutes={delayMinutes}
							onDelayMinutesChange={setDelayMinutes}
							delayError={delayError}
							minDelayMinutes={minDelayMinutes}
						/>
					)}
				</form>
			</PopupSection>

			<PopupSection type="actions" extraClasses={styles.actionsSection}>
				<Button onClick={handleClose}>
					{readOnly
						? MODAL_SET_ON_PROTECTION_TEXT.closeBtn
						: MODAL_SET_ON_PROTECTION_TEXT.cancelBtn}
				</Button>

				{!readOnly && (
					<Button
						type="submit"
						appearance="primary"
						disabled={isSubmitDisabled}
						loading={loading}
						onClick={handleSubmit}
					>
						{MODAL_SET_ON_PROTECTION_TEXT.confirmBtn}
					</Button>
				)}
			</PopupSection>
		</Popup>
	);
};

export default ModalSetOnProtection;
